import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateFireScenarioInput,
  FireExplainResponse,
  FireProjection,
  FireResponse,
  FireScenario,
  GlossaryResponse,
  UpdateFireScenarioInput,
  WhatIfFireInput,
} from "@money/shared";
import { apiFetch } from "./client";

export const fireKey = ["fire"] as const;

export function useFire() {
  return useQuery({
    queryKey: fireKey,
    queryFn: () => apiFetch<FireResponse>("/fire"),
    staleTime: 30_000,
  });
}

export function useCreateFireScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFireScenarioInput) =>
      apiFetch<FireScenario>("/fire/scenarios", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: fireKey }),
  });
}

export function useUpdateFireScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateFireScenarioInput }) =>
      apiFetch<FireScenario>(`/fire/scenarios/${input.id}`, { method: "PATCH", body: input.patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: fireKey }),
  });
}

export function useExplainFire() {
  return useMutation({
    mutationFn: (scenarioId: string) =>
      apiFetch<FireExplainResponse>("/fire/explain", { query: { scenario: scenarioId } }),
  });
}

export function useWhatIf() {
  return useMutation({
    mutationFn: (input: WhatIfFireInput) =>
      apiFetch<FireProjection>("/fire/whatif", { method: "POST", body: input }),
  });
}

export function useGlossary() {
  return useMutation({
    mutationFn: (term: string) =>
      apiFetch<GlossaryResponse>(`/glossary/${encodeURIComponent(term)}`),
  });
}

export function useDeleteFireScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/fire/scenarios/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: fireKey }),
  });
}
